/** Plan 03-15 — PrefRowComponent (visual M3 refresh).
 *
 * Pure presentational row for one event-type pref. Consumes a FormGroup<{ eventType, channel, mode }>
 * passed from the parent NotificationPrefsPageComponent's FormArray; renders:
 *   - Leading semantic icon (Material symbols, color-tinted per category)
 *   - Event label (UI-SPEC §Copywriting Contract → Notification preferences event-label table)
 *   - Event microcopy (when this notification fires)
 *   - Channel select (Email / In-app / Both / None)
 *   - Mode select (Real-time / Daily digest)
 *
 * Layout is a flex row (NOT an HTML <tr>) so the parent can stack rows inside grouped cards
 * with consistent spacing -- HTML tables collapse padding/margins in ways that fight M3.
 *
 * No side effects: parent owns submit + dirty tracking.
 */

import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

/**
 * UI-SPEC verbatim labels for the 9 canonical event types. Keys match the Plan 03-08
 * NepfEventTypes string constants exactly (snake_case).
 */
export const EVENT_LABELS: Readonly<Record<string, string>> = Object.freeze({
  validation_complete: 'Validation complete',
  ready_for_school_signoff: 'Ready for school sign-off',
  ready_for_district_signoff: 'Ready for district sign-off',
  ready_for_state_signoff: 'Ready for state sign-off',
  approved: 'Upload approved',
  rejected: 'Upload rejected',
  window_opening_7d: 'Upload window opens in 7 days',
  window_closing_48h: 'Upload window closes in 48 hours',
  window_closing_7d: 'Upload window closes in 7 days',
  identity_exception_summary: 'Identity resolution summary',
});

/**
 * Per-event microcopy -- explains WHEN the notification fires so the user can decide
 * channel + mode with context. Kept short (1 line @ ~70ch) to honor the readable-line-
 * length rule and keep row height predictable. Not part of UI-SPEC; informational only.
 */
export const EVENT_DESCRIPTIONS: Readonly<Record<string, string>> = Object.freeze({
  validation_complete: 'When your CSV finishes validating (pass or fail) and is ready to review.',
  ready_for_school_signoff: 'When an upload lands in your school sign-off queue.',
  ready_for_district_signoff: 'When an upload lands in your district sign-off queue.',
  ready_for_state_signoff: 'When an upload lands in the state sign-off queue.',
  approved: 'When an upload you uploaded or shepherded reaches approved state.',
  rejected: 'When an upload is rejected and needs your attention.',
  window_opening_7d: 'A 7-day heads-up before an upload window opens.',
  window_closing_48h: 'A 48-hour reminder before an upload window closes.',
  window_closing_7d: 'A 7-day early warning before an upload window closes.',
  identity_exception_summary: 'Daily summary of unmatched educators flagged for triage.',
});

/**
 * Material Symbol name per event type. Picked semantically so the icon glance-reads
 * the kind of event without requiring the label. All from the standard Material Icons
 * set (already loaded for the rest of the SPA -- no font subset change needed).
 */
export const EVENT_ICONS: Readonly<Record<string, string>> = Object.freeze({
  validation_complete: 'fact_check',
  ready_for_school_signoff: 'inbox',
  ready_for_district_signoff: 'inbox',
  ready_for_state_signoff: 'inbox',
  approved: 'verified',
  rejected: 'report',
  window_opening_7d: 'event_available',
  window_closing_48h: 'schedule',
  window_closing_7d: 'schedule',
  identity_exception_summary: 'person_search',
});

/** Channel select options — UI labels and wire-shape values per UI-SPEC + M006 enums. */
export const CHANNEL_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'IN_APP', label: 'In-app' },
  { value: 'BOTH', label: 'Both' },
  { value: 'NONE', label: 'None' },
];

/** quick 260722-n9r (NDE decision #1): events whose EMAIL delivery is mandatory —
 *  mirrors NepfEventTypes.MandatoryEmail server-side (the PUT rejects violations with
 *  EMAIL_REQUIRED_FOR_EVENT; this list only drives the UI lock). */
export const MANDATORY_EMAIL_EVENTS: ReadonlySet<string> = new Set([
  'rejected',
  'ready_for_school_signoff',
  'ready_for_district_signoff',
  'ready_for_state_signoff',
  'window_closing_48h',
  'window_closing_7d',
  'data_correction_requested',
]);

/** Mode select options — UI labels and wire-shape values per UI-SPEC + M006 enums. */
export const MODE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'REAL_TIME', label: 'Real-time' },
  { value: 'DAILY_DIGEST', label: 'Daily digest' },
];

@Component({
  selector: 'nepf-pref-row',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
  ],
  templateUrl: './pref-row.component.html',
  styleUrl: './pref-row.component.scss',
})
export class PrefRowComponent {
  /** FormGroup with controls { eventType: string; channel: string; mode: string }. Parent owns dirty tracking. */
  @Input({ required: true }) formGroup!: FormGroup;

  readonly modeOptions = MODE_OPTIONS;

  /** Channel options for THIS row — mandatory-email events only offer Email/Both. */
  get channelOptions(): ReadonlyArray<{ value: string; label: string }> {
    return this.isMandatoryEmail
      ? CHANNEL_OPTIONS.filter((o) => o.value === 'EMAIL' || o.value === 'BOTH')
      : CHANNEL_OPTIONS;
  }

  /** quick 260722-n9r (NDE decision #1): email cannot be switched off for this event. */
  get isMandatoryEmail(): boolean {
    return MANDATORY_EMAIL_EVENTS.has(this.eventType);
  }

  private get eventType(): string {
    return (this.formGroup?.get('eventType')?.value as string | undefined) ?? '';
  }

  /** Look up the user-facing label for the row's event type (UI-SPEC verbatim). */
  get eventLabel(): string {
    return EVENT_LABELS[this.eventType] ?? this.eventType;
  }

  get eventDescription(): string {
    return EVENT_DESCRIPTIONS[this.eventType] ?? '';
  }

  get eventIcon(): string {
    return EVENT_ICONS[this.eventType] ?? 'notifications';
  }
}
